import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Marker } from "react-map-gl/mapbox";

import { hasValidCoordinates, toMarkerInfo } from "../../history/helpers";

const AssessmentHistoryMarkers = ({
  assessments,
  mapRef,
  selectedAssessmentId,
  setMarkerInfo,
}) => {
  const [visibleAssessments, setVisibleAssessments] = useState([]);
  const filterTimerRef = useRef(null);

  const filterAssessmentsByViewport = useCallback(() => {
    if (!mapRef?.current || !Array.isArray(assessments) || !assessments.length) {
      setVisibleAssessments([]);
      return;
    }

    try {
      const map = mapRef.current.getMap();
      const bounds = map.getBounds();
      const buffer = 0.01;
      const bufferedBounds = {
        north: bounds.getNorth() + buffer,
        south: bounds.getSouth() - buffer,
        east: bounds.getEast() + buffer,
        west: bounds.getWest() - buffer,
      };

      const visible = assessments.filter((assessment) => {
        if (!hasValidCoordinates(assessment)) return false;

        const lat = Number(assessment.latitude);
        const lng = Number(assessment.longitude);
        return (
          lat >= bufferedBounds.south &&
          lat <= bufferedBounds.north &&
          lng >= bufferedBounds.west &&
          lng <= bufferedBounds.east
        );
      });

      setVisibleAssessments(visible.slice(0, 1000));
    } catch (error) {
      setVisibleAssessments(assessments.slice(0, 500));
    }
  }, [assessments, mapRef]);

  useEffect(() => {
    filterAssessmentsByViewport();
  }, [filterAssessmentsByViewport]);

  useEffect(() => {
    if (!mapRef?.current) return;

    const map = mapRef.current.getMap();
    const handleMove = () => {
      if (filterTimerRef.current) {
        clearTimeout(filterTimerRef.current);
      }
      filterTimerRef.current = setTimeout(filterAssessmentsByViewport, 150);
    };

    map.on("move", handleMove);
    map.on("zoom", handleMove);

    return () => {
      map.off("move", handleMove);
      map.off("zoom", handleMove);
      if (filterTimerRef.current) {
        clearTimeout(filterTimerRef.current);
      }
    };
  }, [mapRef, filterAssessmentsByViewport]);

  const markerElements = useMemo(
    () =>
      visibleAssessments.map((assessment) => {
        const isSelected = selectedAssessmentId === assessment.id;

        return (
          <Marker
            key={assessment.id}
            latitude={Number(assessment.latitude)}
            longitude={Number(assessment.longitude)}
            anchor="center"
          >
            <div
              data-history-marker="true"
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setMarkerInfo(toMarkerInfo(assessment));
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  event.stopPropagation();
                  setMarkerInfo(toMarkerInfo(assessment));
                }
              }}
              style={{
                width: isSelected ? 20 : 16,
                height: isSelected ? 20 : 16,
                borderRadius: "50%",
                background: isSelected ? "#ff4444" : "#64b2e6",
                border: "2px solid #fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                cursor: "pointer",
                transition: "all 0.3s ease",
                pointerEvents: "auto",
              }}
            />
          </Marker>
        );
      }),
    [visibleAssessments, selectedAssessmentId, setMarkerInfo],
  );

  return <>{markerElements}</>;
};

export default AssessmentHistoryMarkers;
